import axios from 'axios';
import { LogParser } from 'foreman-slink';
import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { SignedUrlResponse } from 'src/models/signedUrlResponse';
import { AppState } from 'src/redux/types';
import APIS from 'src/utils/apiUrl';
import { getAuthToken } from 'src/utils/auth/getAuthToken';
import { logParserDataToJobLogV2 } from 'src/utils/logs/logHelpers';
import { logActions } from '../actions';
import { createLogRemoteDataByIdSelector } from '../selectors';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

const getJobLogSignedUrl = async (token: string, logId: string): Promise<SignedUrlResponse> => {
  const getSignedUrlResult = await axios({
    method: 'POST',
    url: `${APIS.SENTINEL_API}/api/logs/getsigned`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      logId,
      s3: {
        actionType: 'getObject',
      },
    },
  });

  return getSignedUrlResult.data.data;
};

const fetchSignedJobInfo = async (logId: string) => {
  const auth = await getAuthToken();
  if (!auth) {
    throw new Error('Unable to access current sessions JWT.');
  }
  return await getJobLogSignedUrl(auth.access.token, logId);
};

const ABORT_ERR = 'aborted';

/**
 * A thunk action which fetches and parses a job log for the provided job id.
 * @param id The id of the log to fetch.
 * @param etag A string which uniquely identifies a specific version of a log file.
 * @param forceRefetch Whether or not the log should be fetched regardless of whether it has already been fetched.
 * @param signal An abort signal that can cancel from the outside.
 */
export const fetchLog =
  (
    id: string,
    etag: string | undefined,
    forceRefetch: boolean,
    signal: AbortSignal,
  ): ThunkAction<void, AppState, undefined, AnyAction> =>
  async (dispatch, getState) => {
    const dataSelector = createLogRemoteDataByIdSelector(id);
    const logData = dataSelector(getState());
    const isCacheValid = !!logData && !!etag && logData.etag === etag;
    if (isCacheValid && !forceRefetch) return; // If we've already fetched the data, do nothing.

    const timestamp = Date.now();

    try {
      dispatch(logActions.fetchLogStart({ id, timestamp }));
      const signedRequestInfo = await fetchSignedJobInfo(id);
      const raw = await axios(signedRequestInfo.signedRequest, { signal });
      const lines = (raw.data as string)
        .split(/\r?\n/)
        .map(i => i.trim())
        .filter(i => !!i);
      if (signal.aborted) throw ABORT_ERR;
      dispatch(logActions.fetchLogDownloaded({ id, timestamp, totalLines: lines.length }));

      let hasErrors = false;
      const parser = new LogParser();
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        try {
          parser.Parse(line);
        } catch (e) {
          hasErrors = true;
        }

        if (i % 1000 === 0) {
          if (signal.aborted) throw ABORT_ERR;
          dispatch(logActions.fetchLogParseProgress({ id, parsedLines: i + 1, timestamp }));
          await new Promise(res => window.requestAnimationFrame(res));
        }
      }
      dispatch(
        logActions.fetchLogSuccess({ id, etag, data: logParserDataToJobLogV2(parser.data), hasErrors, timestamp }),
      );
    } catch (e) {
      if (e === ABORT_ERR) dispatch(logActions.fetchLogReset({ id, timestamp }));
      else dispatch(logActions.fetchLogFailure({ id, error: e instanceof Error ? e.message : undefined, timestamp }));
    }
  };

export function useParsedLog(id?: string, etag?: string, forceRefetch = false) {
  const abortCtrl = React.useRef<AbortController | undefined>(undefined);
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();

  // An effect that fetches and parses the raw job log for non-live jobs.
  // Additionally, triggers each time the job's s3 etag changes, indicating a new file has been uploaded.
  React.useEffect(() => {
    if (!id) return; // We cannot fetch the job data until we have the info from the sentinel system.

    const abort = new AbortController();
    abortCtrl.current = abort;
    dispatch(fetchLog(id, etag, forceRefetch, abort.signal));

    return () => {
      abort.abort();
    };
  }, [dispatch, id, etag, forceRefetch]);

  const dataSelector = React.useMemo(() => {
    return id ? createLogRemoteDataByIdSelector(id) : () => undefined;
  }, [id]);

  const data = useSelector(dataSelector);

  return { abortCtrl, data };
}
